/**
 * apps/web/src/lib/api.ts
 *
 * The single axios instance every typed api module uses.
 *
 * Contract with the API (packages/shared/src/api.ts):
 *   Success: { success: true,  data: T, message?: string }
 *   Failure: { success: false, message: string, error: { code, message, field?, requestId?, details? } }
 *
 * Responsibilities:
 *   - baseURL "/api/v1" (the Vite dev proxy rewrites /api → API root, so this
 *     reaches /v1 on the Express server; see vite.config.ts).
 *   - withCredentials so the httpOnly `glarno_session` cookie rides along.
 *   - A response interceptor that UNWRAPS the envelope: callers receive `data`
 *     directly, never the wrapper.
 *   - On failure it throws a typed `ApiError` carrying { code, field, requestId }.
 *   - On 401 (outside the login flow) it redirects to /login.
 */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
} from 'axios';
import type { OrchestrateBody, AiProgressEvent } from '@glarno/contracts/types';
import { shouldRedirectOnUnauthorized } from './authPaths';
import { parseSseStream } from './sseClient';

// ── Envelope types (mirror @glarno/shared, kept local to avoid a workspace dep) ─

interface ApiErrorPayload {
  readonly code: string;
  readonly message: string;
  readonly field?: string;
  readonly requestId?: string;
  readonly details?: unknown;
}

interface ApiSuccessEnvelope<T> {
  readonly success: true;
  readonly data: T;
  readonly message?: string;
}

interface ApiFailureEnvelope {
  readonly success: false;
  readonly message: string;
  readonly error: ApiErrorPayload;
}

type ApiEnvelope<T> = ApiSuccessEnvelope<T> | ApiFailureEnvelope;

/** Page-based list payload returned by every `list*` endpoint. */
export interface PaginatedResult<T> {
  readonly items: readonly T[];
  readonly total: number;
  readonly page: number;
  readonly limit: number;
}

// ── Typed error ────────────────────────────────────────────────────────────────

/**
 * The error thrown by every api module call. Components/React Query catch this
 * and can branch on `code` / surface `message` / show the `requestId` in support.
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly field?: string;
  readonly requestId?: string;
  readonly details?: unknown;

  constructor(args: {
    message: string;
    code: string;
    status: number;
    field?: string;
    requestId?: string;
    details?: unknown;
  }) {
    super(args.message);
    this.name = 'ApiError';
    this.code = args.code;
    this.status = args.status;
    if (args.field !== undefined) this.field = args.field;
    if (args.requestId !== undefined) this.requestId = args.requestId;
    if (args.details !== undefined) this.details = args.details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/** Narrowing helper for `catch (e)` blocks. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// ── Config ───────────────────────────────────────────────────────────────────

export const API_BASE_URL = ((import.meta as unknown as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL) ?? '/api/v1';
const LOGIN_PATH = '/login';

function isFailureEnvelope(body: unknown): body is ApiFailureEnvelope {
  return (
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    (body as { success: unknown }).success === false
  );
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === LOGIN_PATH) return;
  window.location.assign(LOGIN_PATH);
}

/** Build an `ApiError` from an axios failure (envelope, network, or unknown). */
function toApiError(error: AxiosError): ApiError {
  const status = error.response?.status ?? 0;
  const body = error.response?.data as unknown;

  if (isFailureEnvelope(body)) {
    const payload = body.error;
    return new ApiError({
      message: payload.message || body.message,
      code: payload.code,
      status,
      ...(payload.field !== undefined ? { field: payload.field } : {}),
      ...(payload.requestId !== undefined ? { requestId: payload.requestId } : {}),
      ...(payload.details !== undefined ? { details: payload.details } : {}),
    });
  }

  if (status === 0 || error.code === 'ERR_NETWORK') {
    return new ApiError({
      message: 'Impossibile contattare il server. Controlla la connessione.',
      code: 'NETWORK_ERROR',
      status: 0,
    });
  }

  return new ApiError({
    message: error.message || 'Si e verificato un errore imprevisto.',
    code: 'INTERNAL_ERROR',
    status: status || 500,
  });
}

// ── Instance ─────────────────────────────────────────────────────────────────

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

api.interceptors.response.use(
  // Success path: unwrap the envelope so callers get `data` directly.
  (response: AxiosResponse): AxiosResponse => {
    const body = response.data as ApiEnvelope<unknown> | undefined;
    if (body && typeof body === 'object' && 'success' in body && body.success === true) {
      return { ...response, data: (body as ApiSuccessEnvelope<unknown>).data };
    }
    // 204 No Content and non-enveloped responses pass through untouched.
    return response;
  },
  // Failure path: redirect on 401, then reject with a typed ApiError.
  (error: AxiosError): Promise<never> => {
    const status = error.response?.status ?? 0;
    if (status === 401 && shouldRedirectOnUnauthorized(error.config?.url)) {
      redirectToLogin();
    }
    return Promise.reject(toApiError(error));
  },
);

/**
 * Thin generic helpers so api modules read cleanly and stay strongly typed.
 * Each returns the already-unwrapped `data` payload of type `T`.
 */
/**
 * Query params object. Modules pass their typed `List*Params` interfaces here;
 * we accept any plain object (axios serializes it and drops `undefined` keys).
 * `object` is used deliberately so named readonly interfaces (which lack an
 * index signature) are still assignable.
 */
export async function apiGet<T>(url: string, params?: object): Promise<T> {
  const res = await api.get<unknown, AxiosResponse<T>>(url, params ? { params } : undefined);
  return res.data;
}

/** Optional per-call write options. `idempotencyKey` lets the offline outbox
 *  replay a queued mutation without the server executing it twice. */
export interface WriteOptions {
  readonly idempotencyKey?: string;
}

function writeConfig(opts?: WriteOptions): { headers: Record<string, string> } | undefined {
  return opts?.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : undefined;
}

export async function apiPost<T>(url: string, body?: unknown, opts?: WriteOptions): Promise<T> {
  const res = await api.post<unknown, AxiosResponse<T>>(url, body, writeConfig(opts));
  return res.data;
}

export async function apiPatch<T>(url: string, body?: unknown, opts?: WriteOptions): Promise<T> {
  const res = await api.patch<unknown, AxiosResponse<T>>(url, body, writeConfig(opts));
  return res.data;
}

export async function apiPut<T>(url: string, body?: unknown, opts?: WriteOptions): Promise<T> {
  const res = await api.put<unknown, AxiosResponse<T>>(url, body, writeConfig(opts));
  return res.data;
}

export async function apiDelete<T = void>(url: string, opts?: WriteOptions): Promise<T> {
  const res = await api.delete<unknown, AxiosResponse<T>>(url, writeConfig(opts));
  return res.data;
}

// ── Streaming (SSE) ────────────────────────────────────────────────────────────
// axios cannot stream a response body in the browser, so the AI streaming call
// uses fetch directly. Auth rides the same httpOnly cookie via credentials.

/**
 * POST /ai/orchestrate/stream and return an async generator of progress frames.
 * Pre-header failures (auth/entitlement/validation) reject with a typed ApiError;
 * post-header failures arrive as an `error` event in the stream.
 */
export async function streamOrchestrate(
  body: OrchestrateBody,
  signal?: AbortSignal,
): Promise<AsyncGenerator<AiProgressEvent>> {
  const res = await fetch(`${API_BASE_URL}/ai/orchestrate/stream`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  });

  if (!res.ok || !res.body) {
    let message = 'Streaming AI request failed';
    let code = 'AI_STREAM_FAILED';
    try {
      const payload = (await res.json()) as { error?: { code?: string; message?: string } };
      if (payload.error?.message) message = payload.error.message;
      if (payload.error?.code) code = payload.error.code;
    } catch {
      // non-JSON body — keep the generic message
    }
    throw new ApiError({ message, code, status: res.status });
  }

  return parseSseStream(res.body, signal);
}
